<template>
  <section
    v-if="variants.v1 === variant"
    class="n-section-secondary"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <h2 v-if="vars.titleText" class="tw-mb-12">{{ vars.titleText }}</h2>

    <div class="tw-w-full">
      <neuron-google-map-v2
        v-if="locations && locations.length"
        v-model:markers="locations"
        :marker-color="markerColor"
        :center="mapCenter"
        :zoom="mapZoom"
        style="border-radius: var(--c-rounded); overflow: hidden"
      />
    </div>
  </section>

  <section
    v-if="variants.v2 === variant"
    class="n-section-secondary"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <h2 v-if="vars.titleText" class="tw-mb-12">{{ vars.titleText }}</h2>

    <div class="tw-w-screen">
      <neuron-google-map-v2
        v-if="locations && locations.length"
        v-model:markers="locations"
        :marker-color="markerColor"
        :center="mapCenter"
        :zoom="mapZoom"
        style="display:block; width:100%; height:620px; border-radius: var(--c-rounded); overflow: hidden"
      />
    </div>
  </section>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';
import NeuronGoogleMapV2 from '~/components/common/NeuronGoogleMapV2.vue';

export default defineNuxtComponent({
  name: 'Theme7BranchMapAndContactForm',
  components: { NeuronGoogleMapV2 },
  mixins: [ComponentMixin],

  data() {
    return {
      locations: [] as Array<{
        id: string; branch_id?: any; lat: number; lng: number;
        marker?: string; version?: string; branchName?: string; color?: string;
      }>,
      variants: { v1: 'v1', v2: 'v2' },
      form: null as any,
      mapCenter: { lat: 39.0, lng: 35.0 }, // TR merkezine yakın bir varsayılan
      mapZoom: 10, // yaklaşık varsayılan
    };
  },

  created() {
    this.initMapLocations();
    this.form = this.parseFormV2(this.vars.contactForm);
  },

  watch: {
    // locations değişirse merkezi ve zoom'u yeniden hesapla
    locations: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.updateCenterAndZoom(newVal || []);
      },
    },
  },

  computed: {
    markerColor() {
      return this.colors.purple;
    },
    variant() {
      return useVariant(this.component);
    },
  },

  methods: {
    initMapLocations() {
      const branches = this.groupedVariables?.branches || [];

      if (branches.length > 0) {
        this.locations = branches
          .filter((b: any) => this.isValidCoord(b?.lat, b?.long))
          .map((branch: any, index: number) => ({
            id: `branch-${index}-${branch.id || ''}`,
            branch_id: branch.id,
            lat: Number(branch.lat),
            lng: Number(branch.long),
            marker: branch.marker || this.vars?.locationMarkerImage,
            version: 'v3',
            branchName: branch.name,
            color: branch.marker || 'purple',
          }));
      } else if (this.isValidCoord(this.vars?.locationLatNumber, this.vars?.locationLngNumber)) {
        this.locations = [
          {
            id: 'single-location',
            branch_id: this.vars.branchId,
            lat: Number(this.vars.locationLatNumber),
            lng: Number(this.vars.locationLngNumber),
            marker: this.vars.locationMarkerImage,
            version: this.vars.mapVersionText,
            branchName: this.vars.branchNameText,
            color: 'purple',
          },
        ];
      } else {
        this.locations = []; // geçersiz koordinat varsa boş bırak
      }
    },

    updateCenterAndZoom(points: Array<{lat:number; lng:number}>) {
      if (!points.length) return;

      if (points.length === 1) {
        this.mapCenter = { lat: points[0].lat, lng: points[0].lng };
        this.mapZoom = 15;
        return;
      }

      // 1) Coğrafi merkez (basit centroid: ortalama lat/lng)
      const { sumLat, sumLng } = points.reduce(
        (acc, p) => ({ sumLat: acc.sumLat + p.lat, sumLng: acc.sumLng + p.lng }),
        { sumLat: 0, sumLng: 0 }
      );
      const centroid = { lat: sumLat / points.length, lng: sumLng / points.length };
      this.mapCenter = centroid;

      // 2) Yaklaşık zoom (lat/lng span’e göre)
      const lats = points.map(p => p.lat);
      const lngs = points.map(p => p.lng);
      const latSpan = Math.max(...lats) - Math.min(...lats);
      const lngSpan = Math.max(...lngs) - Math.min(...lngs);
      const span = Math.max(latSpan, lngSpan);

      // kaba bir ölçekleme (küresel seviyeden şehir içine)
      // <0.01 ~ sokak, <0.05 ~ ilçe, <0.2 ~ şehir, <1 ~ ülke, >=1 ~ kıta
      let z = 12;
      if (span >= 10) z = 3;
      else if (span >= 5) z = 4;
      else if (span >= 2) z = 6;
      else if (span >= 1) z = 7;
      else if (span >= 0.5) z = 8;
      else if (span >= 0.2) z = 10;
      else if (span >= 0.1) z = 11;
      else if (span >= 0.05) z = 12;
      else if (span >= 0.02) z = 13;
      else z = 14;

      this.mapZoom = z;
    },

    isValidCoord(lat: any, lng: any) {
      const la = Number(lat), ln = Number(lng);
      return Number.isFinite(la) && Number.isFinite(ln) && la <= 90 && la >= -90 && ln <= 180 && ln >= -180;
    },
  },
});
</script>
